import config from '@/config/config';
import { eventHub } from '@/main';
import axios from 'axios';
import httpClient from './http-client';

const list = (filter) => {
	const params = {
		query: filter,
	};

	return httpClient.invoke('client', 'list', params);
};

const get = (clientId, filter = {}) => {
	const params = {
		query: filter,
	};

	params.query.clientId = clientId;

	return httpClient.invoke('client', 'get', params);
};

const add = (data) => {
	const params = {
		data: data,
	};

	return httpClient.invoke('client', 'add', params);
};

const update = (clientId, data) => {
	const params = {
		data: data,
	};

	params.query = {
		clientId,
	};

	return httpClient.invoke('client', 'update', params);
};

const remove = (clientId) => {
	const params = {
		query: {
			clientId: clientId,
		},
	};

	return httpClient.invoke('client', 'remove', params);
};

const getFile = (clientId, fileId) => {
	const params = {
		query: {
			clientId,
			fileId,
		},
	};

	return httpClient.invoke('client', 'getFile', params);
};

const removeFile = (clientId, fileId) => {
	const params = {
		query: {
			clientId,
			fileId,
		},
	};

	return httpClient.invoke('client', 'removeFile', params);
};
const testFont = (fontPath, fallbackFontPath) => {
	return new Promise((resolve) => {
		const font = new FontFace('Roboto', `url(${fontPath})`);
		font.load().then((loadedFace) => {
			document.fonts.add(loadedFace);
			return resolve(fontPath);
		});

		font.onerror = () => {
			return resolve(fallbackFontPath);
		};
	});
};
const testImage = (imagePath, fallbackImagePath) => {
	return new Promise((resolve) => {
		const image = new Image();
		image.addEventListener('load', () => {
			return resolve(imagePath);
		});
		image.addEventListener('error', () => {
			return resolve(fallbackImagePath);
		});
		image.src = imagePath;
	});
};

const loadTheme = async (key, type) => {
	let themeOptions = {};

	clearTheme();

	const defaultLogoUrl = '/img/logo-default.png';
	const defaultBackgroundUrl = '/img/bg-default.jpg';
	const defaultFontUrl = '/fonts/materialdesignicons-webfont.woff2';

	if (key) {
		let params = { query: {} };
		let result;

		if (type === 'clientId') {
			params.query = {
				clientId: key,
			};
			result = await httpClient.invoke('client', 'get', params);
		} else if (type === 'slug') {
			params.query = {
				slug: key,
			};
			result = await httpClient.invoke('client', 'getTheme', params);
		}

		const slug = result.themeSlug;
		const logoFileId = result.logo;
		const backgroundFileId = result.background;
		const fontFieldId = result.font;

		const logoUrl = logoFileId
			? `${config.apiURL}/clients/theme/${slug}/files/${logoFileId}?clientId=${process.env.VUE_APP_CLIENT_ID}`
			: defaultLogoUrl;
		const backgroundUrl = backgroundFileId
			? `${config.apiURL}/clients/theme/${slug}/files/${backgroundFileId}?clientId=${process.env.VUE_APP_CLIENT_ID}`
			: defaultBackgroundUrl;

		const fontUrl = fontFieldId ? getUrl(fontFieldId) : defaultFontUrl;

		themeOptions = {
			slug: result.themeSlug || 'default',
			slogan: result.themeSlogan,
			logoUrl: await testImage(logoUrl, defaultLogoUrl),
			backgroundUrl: await testImage(backgroundUrl, defaultBackgroundUrl).then(
				(path) => `url(${path})`
			),
			fontUrl: await testFont(fontUrl, defaultFontUrl),
			fontId: fontFieldId,
			primaryColor: result.themePrimaryColor,
			secondaryColor: result.themeSecondaryColor,
			tradeName: result.tradeName,
			supportWhatsapp: result.supportWhatsapp,
			supportEmail: result.supportEmail,
			documentationUrl: result.documentationUrl,
			jiraUrl: result.jiraUrl,
			ticketUrl: result.ticketUrl,
			supportWhatsappText: result.supportWhatsappText,
			supportEmailText: result.supportEmailText,
			fontWeightDefault: '',
		};
	} else {
		themeOptions = {
			slogan: '',
			logoUrl: defaultLogoUrl,
			backgroundUrl: `url('${defaultBackgroundUrl}')`,
			fontUrl: defaultFontUrl,
		};
	}

	if (!themeOptions.primaryColor) {
		themeOptions.primaryColor = '#4b2e8b';
	}
	if (!themeOptions.secondaryColor) {
		themeOptions.secondaryColor = '#16BAAF';
	}

	themeOptions = fontWeightBase(themeOptions);

	localStorage.setItem('THEME_CONFIG', JSON.stringify(themeOptions));

	applyTheme();

	eventHub.$emit('THEME_UPDATED', themeOptions);

	return Promise.resolve(true);
};

const applyFont = async (fontPath) => {
	const themeOptions = getTheme();
	if (themeOptions.fontId) {
		try {
			const getToken = sessionStorage.getItem('ACCESS_TOKEN');
			const response = await axios
				.get(fontPath, {
					method: 'GET',
					headers: {
						Authorization: `Bearer ${getToken}`,
						clientId: process.env.VUE_APP_CLIENT_ID,
					},
				})
				.then((res) => res)
				.catch((error) => console.error(error));
			// download font file and save in fonts folder
			const blob = await response.blob();
			const url = URL.createObjectURL(blob);

			const documentStyle = document.documentElement.style;
			documentStyle.setProperty('--v-font-base', `url(${url})`);

			// create font-face
			const fontFace = new FontFace('Roboto', `url(${url}) format('woff2')`);
			await fontFace.load().then((loadedFace) => {
				document.fonts.add(loadedFace);
			});
		} catch (error) {
			console.error(error);
		}
	}
};

const fontWeightBase = (themeOptions) => {
	const documentStyle = document.documentElement.style;

	if (themeOptions && themeOptions.fontId) {
		document.body.classList.add('has-font-id');
		themeOptions.fontWeightDefault = 'normal';

		documentStyle.setProperty(
			'--v-font-weight-base',
			themeOptions.fontWeightDefault
		);
	} else {
		document.body.classList.remove('has-font-id');
	}

	return themeOptions;
};

const applyTheme = () => {
	let themeOptions = getTheme();

	applyBackground(themeOptions.backgroundUrl);
	applyFont(themeOptions.fontUrl);

	let documentStyle = document.documentElement.style;
	if (themeOptions.primaryColor) {
		if (themeOptions.primaryColor) {
			documentStyle.setProperty('--v-primary-base', themeOptions.primaryColor);
		}

		if (themeOptions.secondaryColor) {
			documentStyle.setProperty(
				'--v-secondary-base',
				themeOptions.secondaryColor
			);
		}
	}
	if (!themeOptions.fontId) {
		documentStyle.setProperty('--v-font-base', 'Roboto');
	}

	themeOptions = fontWeightBase(themeOptions);

	eventHub.$emit('THEME_UPDATED', themeOptions);
};

const applyBackground = (backgroundUrl) => {
	let container = document.getElementsByClassName('v-main');

	if (container && container.length) {
		const classAttribute = container[0].getAttribute('class');
		const isHomePath = classAttribute.indexOf('home-path') >= 0;
		const style = container[0].style;

		if (isHomePath) {
			style.backgroundImage = backgroundUrl;
			style.backgroundSize = 'cover';
		} else {
			style.backgroundImage = 'none';
		}
	}
};

const updateFont = (isUpdate) => {
	if (isUpdate) {
		window.location.reload();
	}
};

const getTheme = () => {
	const json = localStorage.getItem('THEME_CONFIG');

	if (json) {
		return JSON.parse(json);
	}

	return {};
};

const clearTheme = () => {
	localStorage.removeItem('THEME_CONFIG');
};

const getUrl = (fieldId) => {
	return fieldId
		? `${config.apiURL}/clients/theme/files/${fieldId}?clientId=${process.env.VUE_APP_CLIENT_ID}`
		: '';
};

const updateThemeConfig = (key, type) => {
	loadTheme(key, type);
};

export default {
	list,
	get,
	add,
	update,
	remove,
	getFile,
	removeFile,
	loadTheme,
	applyTheme,
	getTheme,
	clearTheme,
	updateThemeConfig,
	updateFont,
};
